"use client";

import { useEffect, useState } from "react";
import { Check, Link2, MessageCircle, Share2 } from "lucide-react";
import { baseUrl } from "@/lib/site";
import { cn } from "@/lib/utils";

const LinkedInGlyph = () => (
  <svg viewBox="0 0 24 24" className="w-[17px] h-[17px] fill-current" aria-hidden="true">
    <path d="M4.98 3.5a2.5 2.5 0 1 1-.02 5 2.5 2.5 0 0 1 .02-5zM3 9.2h4V21H3zM9.3 9.2h3.8v1.6h.06c.53-.95 1.82-1.95 3.75-1.95 4 0 4.74 2.6 4.74 6V21h-4v-5.4c0-1.3-.03-2.95-1.82-2.95-1.82 0-2.1 1.4-2.1 2.86V21h-4z" />
  </svg>
);

const FacebookGlyph = () => (
  <svg viewBox="0 0 24 24" className="w-[17px] h-[17px] fill-current" aria-hidden="true">
    <path d="M13.4 21.9v-7.8h2.63l.4-3.05H13.4V9.1c0-.88.25-1.48 1.51-1.48h1.61V4.9c-.28-.04-1.24-.12-2.35-.12-2.33 0-3.92 1.42-3.92 4.02v2.25H7.6v3.05h2.65v7.8z" />
  </svg>
);

/**
 * Teilen-Leiste: LinkedIn, Facebook, X, WhatsApp, Link kopieren, natives Teilen.
 * variant "light" = Glas-Optik für dunkle Hintergründe (Hero).
 */
export default function ShareButtons({
  title,
  path,
  variant = "default",
  showLabel = true,
}: {
  title: string;
  path: string;
  variant?: "default" | "light";
  showLabel?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const [canNativeShare, setCanNativeShare] = useState(false);
  const url = `${baseUrl}${path}`;

  // Erst nach dem Mount prüfen – verhindert Hydration-Mismatch
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCanNativeShare(typeof navigator !== "undefined" && "share" in navigator);
  }, []);

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard nicht verfügbar – Link wird sichtbar markiert
      window.prompt("Link kopieren:", url);
    }
  }

  async function nativeShare() {
    try {
      await navigator.share({ title, url });
    } catch {
      // Abbruch durch Nutzer – nichts zu tun
    }
  }

  const iconButton = cn(
    "w-10 h-10 flex items-center justify-center rounded-xl transition-colors cursor-pointer",
    variant === "light"
      ? "border border-paper/25 bg-paper/10 text-paper backdrop-blur-sm hover:bg-paper/25"
      : "border border-line bg-white text-ink-soft hover:text-accent hover:border-accent"
  );

  return (
    <div className="flex flex-wrap items-center gap-2" aria-label="Artikel teilen">
      {showLabel && (
        <span
          className={cn(
            "text-xs font-bold uppercase tracking-widest mr-1 w-full",
            variant === "light" ? "text-paper/70" : "text-ink-faint"
          )}
        >
          Teilen
        </span>
      )}
      <a
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Auf LinkedIn teilen"
        className={iconButton}
      >
        <LinkedInGlyph />
      </a>
      <a
        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Auf Facebook teilen"
        className={iconButton}
      >
        <FacebookGlyph />
      </a>
      <a
        href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Auf X teilen"
        className={iconButton}
      >
        <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      </a>
      <a
        href={`https://wa.me/?text=${encodeURIComponent(`${title} – ${url}`)}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Per WhatsApp teilen"
        className={iconButton}
      >
        <MessageCircle size={17} aria-hidden="true" />
      </a>
      <button onClick={copyLink} aria-label="Link kopieren" className={iconButton}>
        {copied ? (
          <Check size={17} className={variant === "light" ? "text-paper" : "text-mint"} aria-hidden="true" />
        ) : (
          <Link2 size={17} aria-hidden="true" />
        )}
      </button>
      {canNativeShare && (
        <button onClick={nativeShare} aria-label="Teilen" className={iconButton}>
          <Share2 size={17} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
